import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';

import { TablePaginationProps } from './Pagination.props';

const MAX_VISIBLE_PAGES = 7;

// `current` and `total` here are 1-based page *labels* (1…total). The
// component's public `page` prop is 0-based (it mirrors the data/BFF/URL
// offset), so the wrapper converts to this 1-based label space for windowing
// and display, then converts clicks back to 0-based for `onPageChange`.
const buildPageNumbers = (current: number, total: number): (number | 'ellipsis')[] => {
  if (total <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (current <= 4) {
    pages.push(2, 3, 4, 5, 'ellipsis', total);
  } else if (current >= total - 3) {
    pages.push('ellipsis', total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push('ellipsis', current - 1, current, current + 1, 'ellipsis', total);
  }

  return pages;
};

const PAGE_BTN_BASE = {
  minWidth: 32,
  height: 32,
  fontSize: '12px',
  fontWeight: 500,
  border: '1px solid',
  borderColor: 'v4.border',
  borderRadius: 1,
  px: 1,
} as const;

// `onPageSizeChange` stays on the props (consumers still pass it) but is not
// consumed here: the rows-per-page selector lives in each parent page now.
export const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
}) => {
  const theme = useTheme();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // `page` is 0-based; render and window in 1-based label space.
  const currentPage = page + 1;
  const pages = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 1.5,
        px: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button
          size="small"
          disabled={page <= 0}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
          sx={{
            ...PAGE_BTN_BASE,
            bgcolor: 'background.paper',
            color: 'text.primary',
            '&:hover': { bgcolor: 'v4.tableEven' },
          }}
        >
          &#8249;
        </Button>
        {pages.map((p, idx) =>
          p === 'ellipsis' ? (
            <Typography
              key={`ellipsis-${idx}`}
              sx={{ px: 0.5, fontSize: '12px', color: 'text.secondary' }}
            >
              ...
            </Typography>
          ) : (
            <Button
              key={p}
              size="small"
              variant={p === currentPage ? 'contained' : 'text'}
              onClick={() => onPageChange(p - 1)}
              aria-label={`Page ${p}`}
              aria-current={p === currentPage ? 'page' : undefined}
              sx={{
                ...PAGE_BTN_BASE,
                ...(p === currentPage
                  ? {
                      bgcolor: theme.palette.v4.accent,
                      color: 'common.white',
                      borderColor: theme.palette.v4.accent,
                      '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
                    }
                  : {
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'v4.tableEven' },
                    }),
              }}
            >
              {p}
            </Button>
          )
        )}
        <Button
          size="small"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
          sx={{
            ...PAGE_BTN_BASE,
            bgcolor: 'background.paper',
            color: 'text.primary',
            '&:hover': { bgcolor: 'v4.tableEven' },
          }}
        >
          &#8250;
        </Button>
      </Box>
    </Box>
  );
};
